import {ArgValidator} from './ArgValidator';
import {PromiseRetryOptions} from './CommonTypes';
import {Utils} from './Utils';
import {NumberUtils} from './NumberUtils';

var retry = require('bluebird-retry');

/**
 * @class PromiseUtils - utilities class for promises.
 */
export class PromiseUtils {
    /**
     * @method delay - delay for specified amount of time (milliseconds) using promise.
     * @param ms - milliseconds of delay.
     * @returns {Promise<void>}
     */
    public static delay(ms: number): Promise<any> {
        return Promise.delay(ms);
    }

    /**
     * @method race - race from specified array of promises, ends when at least one promise is fullfiled (or rejected).
     * @param values - array of promises to do race.
     * @returns {Promise<R>}
     */
    public static race<R>(values: Promise.Thenable<R>[]): Promise<R> {
        return Promise.race(values);
    }

    /**
     * @method retry - retries promise operation.
     * @param fn - function which returns promise to be retried when error was throwed from that fn promise.
     * @param options - options to use for promise retry
     * @returns {Promise<T>}
     */
    public static retry<T>(fn: Function, options: PromiseRetryOptions): Promise<T> {
        ArgValidator.ensureIsNotNullOrUndefined(fn, 'fn');
        ArgValidator.ensureIsNotNullOrUndefined(options, 'options');
        ArgValidator.ensureIsNotNullOrUndefined(options.interval, 'options.interval');
        ArgValidator.ensureIsNotNullOrUndefined(options.max_tries, 'options.max_tries');

        return retry(fn, options) as Promise<T>;
    }

    /**
     * Run promise-returning & async functions in series
     * @param list of promises to run in series
     * @returns {Promise<any>} fullfilled promise with array with results of executed promises.
     */
    public static series(list: Array<Promise<any>>) {
        let res: Array<any> = [], p = Promise.resolve();

        return list.reduce(function (pacc: any, fn: any) {
            return pacc.then(fn).then((val: any) => {
                res.push(val);
            });
        }, p).then(() => res);
    }

    /**
     * @method waitFor - waits @condition to be true before resolving promise
     * @param condition - function which returns true or Promise<boolean> to be able to resolve this promise
     * @param interval - interval (in ms) how ofter is condition checked
     * @param maxLoops - maximum number of check loops to be done after which promise is rejected,
     * value 0 disabling max loop check.
     * @returns {Promise<any>|Promise}
     */
    public static waitFor(condition: () => boolean | Promise<boolean>, interval: number, maxLoops?: number) {
        return new Promise<any>((resolve: Function, reject: (error: any) => any) => {
            if (typeof interval !== 'number') {
                throw new Error(`Parameter 'interval' must be number!`);
            }

            if (interval <= 0) {
                throw new Error(`Value 'interval' must be greater than 0, current value is ${interval}!`);
            }

            if (!Utils.isNullOrUndefined(maxLoops) && typeof maxLoops !== 'number') {
                throw new Error(`Parameter 'maxLoops' must be number!`);
            }

            maxLoops = Utils.valueOrDefault(maxLoops, 100);

            let loopIdx = 0;

            const check = () => {
                Promise.resolve()
                       .then(() => {
                           loopIdx++;
                           if (maxLoops > 0 && loopIdx > maxLoops) {
                               throw new Error(`waitFor was rejected after ${loopIdx} loops without valid resolve!`);
                           }
                       })
                       .then(condition).then(val => {
                    if (typeof val !== 'boolean') {
                        throw new TypeError('Expected condition to return a boolean');
                    }

                    if (val === true) {
                        resolve();
                    } else {
                        setTimeout(check, interval);
                    }
                }).catch(reject);
            };

            check();
        });
    }
}


/** WEBPACK FOOTER **
 ** ./Engine/Utils/PromiseUtils.ts
 **/